import { useDeferredValue, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../../styled-system/css";
import { type ColumnDef, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "components/table/usePersistedTableState";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { resetTable, selectionStateToSelectedIds } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { DownloadV4Report } from "../DownloadReport";
import { type TargetQueryV4Row, useGetV4TargetReportByTarget } from "../api/target/v4TargetReportByTarget";
import { useV4TargetReportExport } from "../api/target/v4TargetReportExport";
import TargetDetails from "views/advertising/TargetDetails";
import { formatTargetText } from "utils/formatTargetText";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import {
  AMAZON_TARGET_SOURCE_OPTIONS,
  KEYWORD_MATCH_TYPE_OPTIONS,
  TARGET_MATCH_TYPE_OPTIONS,
  TARGET_STATE_OPTIONS,
  WALMART_TARGET_SOURCE_OPTIONS,
} from "views/advertising/targetLifecycleFilters";
import { BulkActionButton } from "components/table/BulkActionButton";
import useTargetBulkActions from "hooks/useTargetBulkActions";
import { type RowSelectionState, type Updater } from "@tanstack/react-table";
import { type GVAdvertisement } from "modules/advertising/types";
import { useMemo } from "react";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";

const TargetReportTable = ({
  columns,
  state,
  initialState,
  setters,
  targetType = "keyword",
  adFilterOptions = [],
  categoryFilterOptions = [],
  isAdsDataLoading = false,
  adsMapping = {},
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TargetQueryV4Row, any>[];
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  targetType: string;
  adFilterOptions: { value: number | string; label: string }[];
  categoryFilterOptions: { value: number | string; label: string }[];
  isAdsDataLoading?: boolean;
  adsMapping?: Record<string, GVAdvertisement>;
}) => {
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRowsData, setSelectedRowsData] = useState<Record<string, unknown>>({});

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const deferredSearch = useDeferredValue(state.globalFilter);
  const queryClient = useQueryClient();

  const byTargetOptionsGetter = useGetV4TargetReportByTarget(targetType);
  const targetByTargetReportQuery = useQuery(
    byTargetOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
  );

  type ExtendedTargetQueryV4Row = TargetQueryV4Row & { managed?: boolean };

  const tableData: ExtendedTargetQueryV4Row[] = useMemo(() => {
    if (targetByTargetReportQuery.data?.data) {
      const data = targetByTargetReportQuery.data.data.map((target: TargetQueryV4Row) => {
        const ad = adsMapping[target.ad_id];

        return { ...target, managed: ad?.managed?.toLowerCase() === "yes" };
      });
      return data;
    }

    return [];
  }, [targetByTargetReportQuery.data?.data, adsMapping]);

  const unmanagedTargetIds = useMemo(() => {
    return tableData
      .filter((target) => !target.managed)
      .map((target) => target[targetType === "target" ? "adv_target_id" : "adv_keyword_id"]);
  }, [tableData, targetType]);

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const newRowSelection = typeof updater === "function" ? updater(rowSelection) : updater;

    const updatedSelectedRowsData = { ...selectedRowsData };

    for (const row of table.getRowModel().rows) {
      const id = row.id;

      if (newRowSelection[id]) {
        updatedSelectedRowsData[id] = {
          tags: row.original.tags,
          target_source: row.original.source,
          scheduled_actions: row.original.scheduled_actions,
          id: parseInt(id),
          target_id: parseInt(id),
        };
      } else {
        delete updatedSelectedRowsData[id];
      }
    }

    setRowSelection(newRowSelection);
    setSelectedRowsData(updatedSelectedRowsData);
  };

  const exportMutation = useV4TargetReportExport(targetType, state.columnFilters);

  void queryClient.prefetchQuery({
    ...byTargetOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const table = useReactTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => (targetType === "target" ? row.adv_target_id!.toString() : row.adv_keyword_id!.toString()),
    enableRowSelection: (row) => {
      const idAccessor = targetType === "target" ? "adv_target_id" : "adv_keyword_id";
      return !unmanagedTargetIds.includes(row.original[idAccessor]);
    },
    rowCount: targetByTargetReportQuery.data?.count || 0,
    initialState: initialState,
    onRowSelectionChange: handleRowSelectionChange,
    state: { ...state, rowSelection },
    ...Object.fromEntries(Object.entries(setters).filter(([key]) => key !== "onRowSelectionChange")),
    manualPagination: true,
    meta: {
      showSkeleton:
        (targetByTargetReportQuery.isFetching &&
          (!targetByTargetReportQuery.data || targetByTargetReportQuery.isPlaceholderData)) ||
        isAdsDataLoading,
      columnFilterSelectOptions: {
        state: TARGET_STATE_OPTIONS,
        match_type: targetType === "target" ? TARGET_MATCH_TYPE_OPTIONS : KEYWORD_MATCH_TYPE_OPTIONS,
        ad_id: adFilterOptions,
        category_id: categoryFilterOptions,
        source: channel === "walmart" ? WALMART_TARGET_SOURCE_OPTIONS : AMAZON_TARGET_SOURCE_OPTIONS,
      },
    },
  });

  const selected = selectionStateToSelectedIds(table.getState().rowSelection).map((id) => parseInt(id));

  const { bulkActionOptions } = useTargetBulkActions(selected, () => table.resetRowSelection(), targetType, {
    keywords: Object.values(selectedRowsData),
  });

  if (targetByTargetReportQuery.isError) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={targetByTargetReportQuery.error} />;
  }

  return (
    <div>
      <Wrap
        className={css({
          justifyContent: "flex-end",
          width: "100%",
          padding: "small",
          fontSize: "large",
        })}
      >
        <BulkActionButton options={bulkActionOptions} selected={selected} />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          const targetId = row.original[targetType === "target" ? "adv_target_id" : "adv_keyword_id"];
          const formattedRowData = {
            _original: {
              ...row.original,
              target_id: targetId ? targetId : null,
            },
          };

          return (
            <ExpanderContainer className={css({ padding: "medium", backgroundColor: "rgb(239, 239, 239)" })}>
              <TargetDetails
                row={formattedRowData}
                marketPlace={marketPlace}
                channel={channel}
                targetType={targetType}
                formatTargetText={formatTargetText}
              />
            </ExpanderContainer>
          );
        }}
      />
      <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </div>
  );
};

export default TargetReportTable;
